import { ref, type Ref } from 'vue'
import { useAuth } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { LoginCredentials, Message } from '@/types/auth.d'



interface AuthState {
  isLoading: Ref<boolean>
  isSubmitting: Ref<boolean>
  error: Ref<string | null>
}

interface AuthActions {
  handleLogin: (credentials: LoginCredentials) => Promise<void>
  handleOtpSubmit: (otpCode: string) => Promise<void>
  reset: () => void
}

/**
 * Composable for managing authentication login flow
 * Handles username/password login and 2FA OTP submission
 * Provides clean state management without rerenders
 */
export function useAuthLogin(): AuthState & AuthActions {
  // Dependencies
  const authStore = useAuth()
  const { showToast } = useToast()
  const router = useRouter()
  const { t } = useI18n()

  // Local state for loading indicators
  const isLoading = ref(false)
  const isSubmitting = ref(false)
  const error = ref<string | null>(null)

  /**
   * Handle initial login with username and password
   */
  const handleLogin = async (credentials: LoginCredentials): Promise<void> => {
    // Validate inputs
    if (!credentials.username || !credentials.password) {
      error.value = 'نام کاربری یا رمز عبور الزامی است'
      showToast({
        header: t('validation.openError'),
        body: error.value,
        color: 'red'
      })
      return
    }

    // Reset previous state
    error.value = null
    isSubmitting.value = true
    isLoading.value = true

    try {
      // Call store login action
      const result = await authStore.loginAdmin({
        username: credentials.username,
        password: credentials.password
      })

      // Handle 2FA requirement (code 10)
      if (result?.require2fa) {
        // Modal is already opened by store
        isLoading.value = false
        isSubmitting.value = false
        return
      }

      // Handle successful login (no 2FA)
      if (result?.success) {
        await handleSuccessfulAuth()
      } else {
        // Handle login failure
        handleAuthError(result?.message)
      }
    } catch (err) {
      handleAuthError(null, err)
    } finally {
      // Only reset loading if modal isn't being shown
      if (!authStore.showOtpModal) {
        isLoading.value = false
        isSubmitting.value = false
      }
    }
  }

  /**
   * Handle OTP code submission for 2FA
   */
  const handleOtpSubmit = async (otpCode: string): Promise<void> => {
    // Validate OTP code
    if (!otpCode || otpCode.length !== 6) {
      error.value = 'لطفا کد ۶ رقمی را وارد کنید'
      showToast({
        header: t('validation.openError'),
        body: error.value,
        color: 'red'
      })
      return
    }

    error.value = null
    isLoading.value = true

    try {
      // Submit OTP to store
      const result = await authStore.submitOtp(otpCode)

      if (result?.success) {
        // Modal is already closed by store
        await handleSuccessfulAuth()
      } else {
        // Show error but keep modal open for retry
        handleAuthError(result?.message)
      }
    } catch (err) {
      handleAuthError(null, err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Handle successful authentication flow
   */
  const handleSuccessfulAuth = async (): Promise<void> => {
    try {
      // Show success message
      showToast({
        header: 'ورود موفق',
        body: 'به پنل مدیریت خوش آمدید',
        color: 'green'
      })

      // Set loading state for smooth UX transition
      isLoading.value = true
      authStore.loading = true

      // Wait 3 seconds for smooth loading experience
      await new Promise(resolve => setTimeout(resolve, 500))


      await authStore.checkLogin()


      authStore.loading = true


      const redirect = new URLSearchParams(location.search).get('redirect')
      await router.replace(redirect || '/')



      // Small delay to ensure navigation completes smoothly
      await new Promise(resolve => setTimeout(resolve, 200))

      // Cleanup - reset loading states after navigation
      isLoading.value = false
      authStore.loading = false
      reset()
    } catch (err) {
      console.error('Post-auth error:', err)
      isLoading.value = false
      authStore.loading = false
    }
  }

  /**
   * Handle authentication errors
   */
  const handleAuthError = (message?: Message | null, err?: unknown): void => {
    if (message?.header) {
      error.value = message.body
      showToast({
        header: message.header,
        body: message.body,
        color: 'red'
      })
    } else if (err) {
      error.value = err instanceof Error ? err.message : 'خطای نامشخص'
      showToast({
        header: t('validation.openError'),
        body: error.value,
        color: 'red'
      })
    }
  }

  /**
   * Reset composable state
   */
  const reset = (): void => {
    isLoading.value = false
    isSubmitting.value = false
    authStore.showOtpModal = false
    error.value = null
  }

  return {
    // State
    isLoading,
    isSubmitting,
    error,

    // Actions
    handleLogin,
    handleOtpSubmit,
    reset
  }
}
